import React, { useState, useEffect } from 'react';
import {
  Typography,
  Card,
  CardContent,
  Grid,
  Box,
  useTheme,
  Stack,
  Avatar,
} from '@mui/material';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  Legend,
  AreaChart,
  Area,
} from 'recharts';
import {
  ShoppingCart,
  Store,
  Inventory,
  AttachMoney,
  LocationOn,
  TrendingUp,
} from '@mui/icons-material';
import { formatDistanceToNow } from 'date-fns';
import StoreIcon from '@mui/icons-material/Store';
import OnlinePredictionIcon from '@mui/icons-material/OnlinePrediction';
import InventoryIcon from '@mui/icons-material/Inventory';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';

import {
  fetchKPIs,
  fetchSalesTrend,
  fetchInventoryDistribution,
  fetchSalesByLocation,
  fetchCategories,
  fetchLocations,
} from '../api/analyticsApi';

// Modern color palette
const CHART_COLORS = {
  purple: '#6366f1',
  teal: '#14b8a6',
  pink: '#ec4899',
  blue: '#3b82f6',
  gray: '#6b7280',
};

// Define types
type KPI = {
  title: string;
  value: string | number;
  icon: JSX.Element;
  color: string;
};

type SalesData = {
  month: string;
  sales: number;
  goal?: number;
};

type StockData = {
  category: string;
  stock: number;
};

type SalesByLocation = {
  location: string;
  value: number;
};

type Category = {
  id: number;
  name: string;
};

type Location = {
  id: number;
  name: string;
};

interface TooltipProps {
  active?: boolean;
  payload?: Array<{
    name: string;
    value: number;
    color: string;
  }>;
  label?: string;
}

const CustomTooltip: React.FC<TooltipProps> = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <Card variant="outlined">
        <CardContent sx={{ py: 1, px: 1.5 }}>
          <Typography variant="body2" color="textSecondary">{label}</Typography>
          {payload.map((entry, index) => (
            <Typography
              key={index}
              variant="body2"
              sx={{ color: entry.color }}
            >
              {entry.name}: ${entry.value.toLocaleString()}
            </Typography>
          ))}
        </CardContent>
      </Card>
    );
  }
  return null;
};

const CustomTooltip2: React.FC<TooltipProps> = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <Card variant="outlined">
        <CardContent sx={{ py: 1, px: 1.5 }}>
          <Typography variant="body2" color="textSecondary">{label}</Typography>
          {payload.map((entry, index) => (
            <Typography
              key={index}
              variant="body2"
              sx={{ color: entry.color }}
            >
              {entry.name}: {entry.value.toLocaleString()}
            </Typography>
          ))}
        </CardContent>
      </Card>
    );
  }
  return null;
};

const Dashboard: React.FC = () => {
  const theme = useTheme();
  const [kpis, setKpis] = useState<KPI[]>([]);
  const [salesData, setSalesData] = useState<SalesData[]>([]);
  const [stockData, setStockData] = useState<StockData[]>([]);
  const [salesByLocation, setSalesByLocation] = useState<SalesByLocation[]>([]);
  const [categories, setCategories] = useState<Category[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const [lastUpdated, setLastUpdated] = useState<Date>(new Date());
  const [monthOverMonthChange, setMonthOverMonthChange] = useState<number>(0);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [
          kpisRes,
          salesTrendRes,
          inventoryRes,
          salesLocationRes,
          categoriesRes,
          locationsRes,
        ] = await Promise.all([
          fetchKPIs(),
          fetchSalesTrend(),
          fetchInventoryDistribution(),
          fetchSalesByLocation(),
          fetchCategories(),
          fetchLocations(),
        ]);

        setKpis(kpisRes);
        setSalesData(salesTrendRes);
        setStockData(inventoryRes);
        setSalesByLocation(
          salesLocationRes.map((item: { location: string; value: string | number }) => ({
            location: item.location,
            value: Number(item.value),
          }))
        );
        setCategories(categoriesRes);
        setLocations(locationsRes);
        setLastUpdated(new Date());

        // Calculate month-over-month change
        if (salesTrendRes.length > 1) {
          const currentMonth = salesTrendRes[salesTrendRes.length - 1].sales;
          const prevMonth = salesTrendRes[salesTrendRes.length - 2].sales;
          const change = ((currentMonth - prevMonth) / prevMonth) * 100;
          setMonthOverMonthChange(parseFloat(change.toFixed(1)));
        }
      } catch (error) {
        console.error('Error fetching dashboard data:', error);
      }
    };

    fetchData();
    const interval = setInterval(fetchData, 3600000); // Refresh every hour
    return () => clearInterval(interval);
  }, []);

  // Generate dynamic colors for categories and locations
  const generateColors = (items: Array<Category | Location>) => {
    const colors = [CHART_COLORS.purple, CHART_COLORS.teal, CHART_COLORS.blue, CHART_COLORS.pink];
    return items.reduce((acc, item, index) => {
      return {
        ...acc,
        [item.name]: colors[index % colors.length] || CHART_COLORS.gray,
      };
    }, {} as Record<string, string>);
  };

  const categoryColors = generateColors(categories);
  const locationColors = generateColors(locations);

  // Calculate total sales for the center of the pie chart
  const totalSales = salesByLocation.reduce((sum, entry) => sum + Number(entry.value || 0), 0);

  // Format sales values for Y-axis
  const formatYAxis = (value: number) => {
    if (value >= 1000000) return `$${(value / 1000000).toFixed(1)}M`;
    if (value >= 1000) return `$${(value / 1000).toFixed(1)}k`;
    return `$${value}`;
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h5" fontWeight="600" sx={{ mb: 1 }}>
          Business Overview
        </Typography>
        <Typography variant="body2" color="textSecondary">
          Updated {formatDistanceToNow(lastUpdated, { addSuffix: true })} •{' '}
          <TrendingUp sx={{
            fontSize: 14,
            verticalAlign: 'text-top',
            color: monthOverMonthChange >= 0 ? CHART_COLORS.teal : CHART_COLORS.pink
          }} />
          <Box
            component="span"
            sx={{
              color: monthOverMonthChange >= 0 ? CHART_COLORS.teal : CHART_COLORS.pink,
              ml: 0.5
            }}
          >
            {monthOverMonthChange >= 0 ? '+' : ''}{monthOverMonthChange}%
          </Box>{' '}
          vs last month
        </Typography>
      </Box>

      {/* KPI Cards */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {kpis.map((kpi, index) => {
          let icon: JSX.Element = <InventoryIcon />;
          let color = CHART_COLORS.blue;

          if (kpi.title.includes('Online Sales')) {
            icon = <OnlinePredictionIcon />;
            color = CHART_COLORS.purple;
          } else if (kpi.title.includes('In-Store Sales')) {
            icon = <StoreIcon />;
            color = CHART_COLORS.teal;
          } else if (kpi.title.includes('Active Online')) {
            icon = <ShoppingCartIcon />;
            color = CHART_COLORS.pink;
          } else if (kpi.title.includes('Active In-Store')) {
            icon = <InventoryIcon />;
            color = CHART_COLORS.blue;
          } else if (kpi.title === 'Locations') {
            icon = <LocationOnIcon />;
            color = CHART_COLORS.gray;
          } else if (kpi.title === 'Avg. Order Value') {
            icon = <MonetizationOnIcon />;
            color = CHART_COLORS.teal;
          }

          return (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <Card
                sx={{
                  borderRadius: 2,
                  boxShadow: '0px 2px 8px rgba(0,0,0,0.08)',
                  transition: 'transform 0.2s',
                  '&:hover': { transform: 'translateY(-4px)' },
                }}
              >
                <CardContent>
                  <Stack direction="row" alignItems="center" spacing={2}>
                    <Avatar
                      sx={{
                        bgcolor: `${color}20`,
                        color: color,
                        width: 40,
                        height: 40,
                      }}
                    >
                      {icon}
                    </Avatar>
                    <Box>
                      <Typography variant="body2" color="textSecondary">
                        {kpi.title}
                      </Typography>
                      <Typography variant="h6" fontWeight="600">
                        {kpi.value}
                      </Typography>
                    </Box>
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>


      {/* Charts */}
      <Grid container spacing={3}>
        {/* Sales Trend Chart */}
        <Grid item xs={12} md={8}>
          <Card sx={{ borderRadius: 2 }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Sales Trend
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <AreaChart data={salesData}>
                  <defs>
                    <linearGradient id="salesGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor={CHART_COLORS.teal} stopOpacity={0.2} />
                      <stop offset="95%" stopColor={CHART_COLORS.teal} stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" vertical={false} />
                  <XAxis
                    dataKey="month"
                    tickLine={false}
                    axisLine={{ stroke: theme.palette.divider }}
                  />
                  <YAxis
                    axisLine={false}
                    tickLine={false}
                    tickFormatter={formatYAxis}
                  />
                  <Tooltip
                    content={<CustomTooltip />}
                    formatter={(value) => [`$${Number(value).toLocaleString()}`, 'Sales']}
                  />
                  <Area
                    type="monotone"
                    dataKey="sales"
                    stroke={CHART_COLORS.teal}
                    strokeWidth={2}
                    fillOpacity={1}
                    fill="url(#salesGradient)"
                  />
                  <Line
                    type="monotone"
                    dataKey="goal"
                    stroke={CHART_COLORS.gray}
                    strokeDasharray="5 5"
                    strokeWidth={1.5}
                    dot={false}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Inventory Distribution */}
        <Grid item xs={12} md={4}>
          <Card sx={{ borderRadius: 2 }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Inventory Distribution
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <BarChart data={stockData}>
                  <CartesianGrid vertical={false} strokeDasharray="3 3" />
                  <XAxis dataKey="category" axisLine={false} tickLine={false} />
                  <YAxis axisLine={false} tickLine={false} />
                  <Tooltip content={<CustomTooltip2 />} />
                  <Bar dataKey="in_store" name="In-Store" fill={CHART_COLORS.teal} radius={[4, 4, 0, 0]} />
                  <Bar dataKey="online" name="Online" fill={CHART_COLORS.purple} radius={[4, 4, 0, 0]} />

                </BarChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>

        {/* Sales by Location */}
        <Grid item xs={12}>
          <Card sx={{ borderRadius: 2 }}>
            <CardContent>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Sales by Location
              </Typography>
              <ResponsiveContainer width="100%" height={300}>
                <PieChart>
                  <Pie
                    data={salesByLocation}
                    dataKey="value"
                    nameKey="location"
                    cx="50%"
                    cy="50%"
                    innerRadius={60}
                    outerRadius={80}
                    paddingAngle={2}
                  >
                    {salesByLocation.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={locationColors[entry.location]} />
                    ))}
                  </Pie>
                  <Legend
                    layout="horizontal"
                    verticalAlign="bottom"
                    align="center"
                    wrapperStyle={{ paddingTop: 24 }}
                    formatter={(value, entry, index) => {
                      const locationName = salesByLocation[index].location;
                      const formatted = `$${salesByLocation[index].value.toLocaleString()}`;
                      return (
                        <span style={{ color: theme.palette.text.primary }}>
                          {locationName}: {formatted}
                        </span>
                      );
                    }}
                  />
                  <Tooltip
                    content={<CustomTooltip />}
                    formatter={(value) => [`$${Number(value).toLocaleString()}`, 'Sales']}
                  />
                  <text
                    x="50%"
                    y="50%"
                    textAnchor="middle"
                    dominantBaseline="middle"
                    style={{
                      fontSize: '1.2rem',
                      fontWeight: 600,
                      fill: theme.palette.text.primary,
                    }}
                  >
                    ${totalSales.toLocaleString()}
                  </text>
                </PieChart>
              </ResponsiveContainer>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Dashboard;